<template>
	<div id="app">
		<b-loading :active="isLoading"></b-loading>
		<p class="no-mobile-app-info">
			Niestety aplikacja nie jest aktualnie przygotowana do pracy na urządzeniach mobilnych.
			<br />Prosimy o kontakt z autorem w celu dopasowania aplikacji pod swoje preferencje.
		</p>
		<nav-bar
			v-if="isLogged"
			@new-order-click="showNewOrderModal"
			@new-user-click="showNewUserModal"
			@logout-click="logout"
		></nav-bar>

		<router-view
			class="RouterView"
			@map-order-click="showViewOrderModal"
			@order-click="showViewOrderModal"
			@route-click="showViewRouteModal"
			@user-click="showViewUserModal"
		/>

		<b-modal
			:active="isNewUserModalActive"
			has-modal-card
			@close="hideNewUserModal"
			class="NewUserModal"
		>
			<new-user-modal />
		</b-modal>
		<b-modal
			:active="isViewUserModalActive"
			has-modal-card
			@close="hideViewUserModal"
			@order-click="showViewOrderModal"
			class="ViewUserModal"
		>
			<view-user-modal
				:isGmailActivated="isGmailActivated"
				:isGmailDeactivated="isGmailDeactivated"
			/>
		</b-modal>
		<b-modal
			:active="isViewOrderModalActive"
			has-modal-card
			@close="hideViewOrderModal"
			@route-click="showViewRouteModal"
			class="ViewOrderModal"
		>
			<view-order-modal />
		</b-modal>
		<b-modal
			:active="isNewOrderModalActive"
			has-modal-card
			@close="hideNewOrderModal"
			class="NewOrderModal"
		>
			<new-order-modal />
		</b-modal>
		<b-modal
			:active="isViewRouteModalActive"
			has-modal-card
			@close="hideViewRouteModal"
			@order-click="showViewOrderModal"
			class="ViewRouteModal"
			:width="1200"
		>
			<view-route-modal />
		</b-modal>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import bus from '@/EventBus'
import config from '@/config'
import NavBar from '@/components/NavBar'
import UserService from '@/services/UserService'
import InactivityDetector from '@/InactivityDetector'
import NewOrderModal from '@/components/modals/NewOrderModal'
import ViewOrderModal from '@/components/modals/ViewOrderModal'
import ViewRouteModal from '@/components/modals/ViewRouteModal'
import NewUserModal from '@/components/modals/NewUserModal'
import ViewUserModal from '@/components/modals/ViewUserModal'

export default {
	name: 'app',
	data() {
		return {
			isLoading: false,
			isGmailActivated: false,
			isGmailDeactivated: false
		}
	},
	computed: {
		...mapGetters('auth', ['isLogged', 'user']),
		...mapState('modals', [
			'isNewUserModalActive',
			'isViewUserModalActive',
			'isNewOrderModalActive',
			'isViewOrderModalActive',
			'isViewRouteModalActive'
		])
	},
	components: {
		NavBar,
		ViewUserModal,
		NewUserModal,
		ViewRouteModal,
		ViewOrderModal,
		NewOrderModal
	},
	methods: {
		showNewOrderModal() {
			this.$store.dispatch('orders/setInitialOrder', this.user).then(() => {
				this.$store.dispatch('modals/showNewOrderModal')
			})
		},
		showNewUserModal() {
			this.$store.dispatch('modals/showNewUserModal')
		},
		logout(message) {
			if (!this.isLogged) return
			if (this.isNewOrderModalActive) this.$store.dispatch('modals/hideNewOrderModal')
			if (this.isNewUserModalActive) this.$store.dispatch('modals/hideNewUserModal')
			if (this.isViewRouteModalActive) this.$store.dispatch('modals/hideViewRouteModal')
			if (this.isViewOrderModalActive) this.$store.dispatch('modals/hideViewOrderModal')
			if (this.isViewUserModalActive) this.$store.dispatch('modals/hideViewUserModal')

			message = message || 'Wylogowano z systemu.'
			this.isLoading = true
			this.$store.dispatch('auth/logout').then(() => {
				this.isLoading = false
				this.$router.push({ name: 'login' })
				this.$snackbar.open(message)
			})
		},
		showViewUserModal(user) {
			if (this.isViewRouteModalActive) this.$store.dispatch('modals/hideViewRouteModal')
			if (this.isViewOrderModalActive) this.$store.dispatch('modals/hideViewOrderModal')
			this.isLoading = true
			this.$store.dispatch('users/setCurrentUser', user).then(() => {
				this.isLoading = false
				this.$store.dispatch('modals/showViewUserModal')
			})
		},
		hideViewUserModal() {
			this.$store.dispatch('modals/hideViewUserModal')
			this.isGmailActivated = false

			// if URL contains "gmail-activated" query thenredirect to /users
			if (this.$route.query.hasOwnProperty('gmail-activated')) {
				this.$router.push({ name: 'users' })
			}
		},
		hideNewUserModal() {
			this.$store.dispatch('modals/hideNewUserModal')
		},
		showViewOrderModal(order) {
			if (this.isViewUserModalActive) this.$store.dispatch('modals/hideViewUserModal')
			if (this.isViewRouteModalActive) this.$store.dispatch('modals/hideViewRouteModal')
			this.isLoading = true
			this.$store.dispatch('orders/setCurrentOrder', order).then(() => {
				this.isLoading = false
				this.$store.dispatch('modals/showViewOrderModal')
			})
		},
		hideViewOrderModal() {
			this.$store.dispatch('modals/hideViewOrderModal')
		},
		showViewRouteModal(route) {
			if (this.isViewUserModalActive) this.$store.dispatch('modals/hideViewUserModal')
			if (this.isViewOrderModalActive) this.$store.dispatch('modals/hideViewOrderModal')
			this.isLoading = true
			this.$store.dispatch('routes/setCurrentRoute', route).then(() => {
				this.isLoading = false
				this.$store.dispatch('modals/showViewRouteModal')
			})
		},
		hideViewRouteModal() {
			this.$store.dispatch('modals/hideViewRouteModal')
		},
		hideNewOrderModal() {
			this.$store.dispatch('modals/hideNewOrderModal')
		}
	},
	created() {
		console.log('App started using config: ', config)

		bus.$on('new-orders-loaded', (totalNewOrders) => {
			this.totalNewOrders = totalNewOrders
		})
		bus.$on('logout', (message) => this.logout(message))
	},
	mounted() {
		const onInactivityAction = () => {
			if (this.isLogged) bus.$emit('logout', 'Wylogowano z powodu nieaktywności od 2h.')
		}

		const oneHour = 1000 * 60 * 60
		const inactivityDetector = new InactivityDetector(onInactivityAction, 2 * oneHour)
		inactivityDetector.activate()

		// check if "gmail-activated" param is set in url hash
		const gmailActivatedUserId = this.$route.query['gmail-activated']
		if (gmailActivatedUserId) {
			this.isLoading = true
			return UserService.find(gmailActivatedUserId).then((response) => {
				this.$store.dispatch('users/setCurrentUser', response.data).then(() => {
					this.isLoading = false
					if (response.data.googleEmail && response.data.googleRefreshToken) {
						this.isGmailActivated = true
						this.$store.dispatch('modals/showViewUserModal')
					}
				})
			})
		}

		// check if "gmail-deactivated" param is set in url hash
		const gmailDeactivatedUserId = this.$route.query['gmail-deactivated']
		if (gmailDeactivatedUserId) {
			this.isLoading = true
			return UserService.find(gmailDeactivatedUserId).then((response) => {
				this.$store.dispatch('users/setCurrentUser', response.data).then(() => {
					this.isLoading = false
					if (!response.data.googleEmail && !response.data.googleRefreshToken) {
						this.isGmailDeactivated = true
						this.$store.dispatch('modals/showViewUserModal')
					}
				})
			})
		}
	}
}
</script>

<style lang="scss">
@font-face {
	font-family: 'Material Icons';
	font-style: normal;
	font-weight: 400;
	src: url('assets/fonts/materialicons/MaterialIcons-Regular.eot'); /* For IE6-8 */
	src: local('assets/fonts/materialicons/Material Icons'),
		local('assets/fonts/materialicons/MaterialIcons-Regular'),
		url('assets/fonts/materialicons/MaterialIcons-Regular.woff2') format('woff2'),
		url('assets/fonts/materialicons/MaterialIcons-Regular.woff') format('woff'),
		url('assets/fonts/materialicons/MaterialIcons-Regular.ttf') format('truetype');
}

.mdi {
	font-family: 'Material Icons';
	font-weight: normal;
	font-style: normal;
	font-size: 24px; /* Preferred icon size */
	display: inline-block;
	line-height: 1;
	text-transform: none;
	letter-spacing: normal;
	word-wrap: normal;
	white-space: nowrap;
	direction: ltr;
	text-rendering: optimizeLegibility;
	font-feature-settings: 'liga';
}

html,
body,
#app {
	height: 100%;
}
html {
	overflow: auto;
}
body {
	font-size: 0.9rem;
}
#app {
	display: flex;
	flex-direction: column;
	> div {
		@media (max-width: 1099px) {
			// display: none;
		}
	}
}
.no-mobile-app-info {
	padding: 20px;
	background-color: #3273dc;
	color: #fff;
	text-align: center;
	@media (min-width: 1100px) {
		display: none;
	}
}
.field-body.is-grouped {
	flex-direction: column;
}
.modal {
	z-index: 4000;
}
.notices {
	z-index: 5000;
}
.modal-card {
	width: auto;
	h2 {
		color: #666;
		font-size: 0.9rem;
		font-weight: bold;
	}
	.dates {
		font-size: 12px;
		margin-bottom: 20px;
	}
	footer .level {
		width: 100%;
	}
	.mdi {
		font-size: 19px;
		padding-right: 4px;
		vertical-align: middle;
	}
	.dropdown-trigger .mdi {
		padding-right: 0;
		// padding-bottom: 4px;
	}
	.group {
		border: 1px solid #eee;
		border-radius: 6px;
		margin: 20px 0;
		// padding: 25px;
	}
	.column {
		font-size: 0.9rem;
	}
	.group > .field {
		padding: 0 25px;
		&:first-child {
			padding-top: 25px;
		}
		&:last-child {
			padding-bottom: 25px;
		}
	}
	.field-label {
		text-align: left;
		color: #aaa;
	}
	.field-body {
		flex-grow: 2.5;
	}
	.my-gallery {
		img {
			display: block;
		}
		a {
			display: block;
			transition: opacity 0.3s;
			&:hover {
				opacity: 0.6;
			}
		}
	}
}
#route-map .custom-marker {
	text-shadow: 0 0 5px #fff;
	text-align: center;
	opacity: 1;
	transition: opacity 0.5s;
	&.is-hidden {
		visibility: hidden;
		opacity: 0;
	}

	&.is-label-hidden span {
		visibility: hidden;
		opacity: 0;
	}

	span {
		display: block;
		font-weight: bold;
		font-size: 11px;
		background-color: #fff;
		border: 1px solid #000;
		padding: 0px;
		line-height: 16px;
		opacity: 1;
		transition: opacity 0.5s;
	}
	i {
		font-size: 36px;
	}
}
</style>
